import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  notificationApi,
  type AdminNotificationItem,
} from "@/lib/api/notifications";
import { useNotificationSummary } from "@/hooks/useNotificationSummary";

function priorityClass(priority: string) {
  switch (priority) {
    case "urgent":
      return "bg-destructive/10 text-destructive border-destructive/20";
    case "high":
      return "bg-warning/10 text-warning border-warning/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { summary, refresh } = useNotificationSummary();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AdminNotificationItem[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    void notificationApi
      .list({ status: "Unread" })
      .then((res) => setItems(res.data.items.slice(0, 8)))
      .catch(() => setItems([]));
  }, [open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const unread = summary?.unread ?? 0;

  return (
    <div className="relative group-data-[collapsible=icon]:hidden" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative rounded-full"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute bottom-12 right-0 z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-elevated">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Latest unread</p>
              <p className="text-xs text-muted-foreground">{unread} unread</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={async () => {
                const keys = items.map((i) => i.sourceKey);
                if (keys.length) {
                  await notificationApi.markRead(keys);
                  refresh();
                  setItems([]);
                }
              }}
            >
              Mark all read
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {!items.length ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                All caught up.
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.sourceKey}
                  type="button"
                  className="block w-full border-b border-border px-4 py-3 text-left last:border-0 hover:bg-accent/60"
                  onClick={() => {
                    setOpen(false);
                    void notificationApi.markRead([item.sourceKey]).finally(() => {
                      refresh();
                      navigate(item.href || "/notifications");
                    });
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium">{item.title}</span>
                    <Badge
                      variant="outline"
                      className={cn("shrink-0 text-[10px] uppercase", priorityClass(item.priority))}
                    >
                      {item.priority}
                    </Badge>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {item.description}
                  </p>
                </button>
              ))
            )}
          </div>
          <div className="flex items-center justify-between border-t border-border bg-muted/40 px-3 py-2">
            <Button size="sm" asChild onClick={() => setOpen(false)}>
              <Link to="/notifications">View all</Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
